// ═══════════════════════════════════════════════════════
//  CONSULTEC — Auth Service
//  src/services/auth.js
//  Gestión de autenticación y permisos por rol
// ═══════════════════════════════════════════════════════

import { getFirebase } from './firebase.js'
import { DEMO_USERS }  from '../utils/demoData.js'

// ── ROLES Y PERMISOS ─────────────────────────────────────
export const ROLES = {
  DIRECTOR:     'director',
  SUPERVISOR:   'supervisor',
  RESIDENTE:    'residente',
  ADMIN:        'administrativo',
  CLIENTE:      'cliente',
  CONTRATISTA:  'contratista'
}

export const PERMISOS = {
  [ROLES.DIRECTOR]: {
    label:       'Director / Gerente',
    icon:        '🏢',
    canReadAll:  true,
    canWrite:    false,    // No edita registros de campo
    canExport:   true,
    canApprove:  true,
    canSign:     true,
    canSeeFinanciero: true,
    canSeeCierre:     true,
    canSeeAllProjects: true,
    descripcion: 'Acceso de lectura total + exportación. No edita registros de campo.'
  },
  [ROLES.SUPERVISOR]: {
    label:       'Supervisor de obra',
    icon:        '👷',
    canReadAll:  true,
    canWrite:    true,     // Solo sus propios registros
    canWriteOwn: true,
    canExport:   false,
    canApprove:  false,
    canSign:     false,
    canSeeFinanciero: false,
    canSeeCierre:     false,
    canSeeAllProjects: false,
    descripcion: 'Registra y edita solo sus propios datos de campo.'
  },
  [ROLES.RESIDENTE]: {
    label:       'Residente de obra',
    icon:        '📋',
    canReadAll:  true,
    canWrite:    true,
    canWriteOwn: true,
    canExport:   false,
    canApprove:  false,
    canSign:     false,
    canSeeFinanciero: false,
    canSeeCierre:     true,
    canSeeAllProjects: false,
    descripcion: 'Registra su sección y valida avances en sitio.'
  },
  [ROLES.ADMIN]: {
    label:       'Administrativo',
    icon:        '📊',
    canReadAll:  true,
    canWrite:    false,
    canExport:   true,
    canApprove:  true,
    canSign:     false,
    canSeeFinanciero: true,
    canSeeCierre:     false,
    canSeeAllProjects: true,
    descripcion: 'Gestiona finanzas, documentos y estimaciones.'
  },
  [ROLES.CLIENTE]: {
    label:       'Cliente / Propietario',
    icon:        '👔',
    canReadAll:  false,    // Solo su proyecto
    canWrite:    false,
    canExport:   false,
    canApprove:  false,
    canSign:     true,     // Puede firmar entregables
    canSeeFinanciero: false,
    canSeeCierre:     false,
    canSeeAllProjects: false,
    descripcion: 'Solo lectura del proyecto asignado.'
  },
  [ROLES.CONTRATISTA]: {
    label:       'Contratista',
    icon:        '🔧',
    canReadAll:  false,
    canWrite:    true,
    canWriteOwn: true,
    canExport:   false,
    canApprove:  false,
    canSign:     false,
    canSeeFinanciero: false,
    canSeeCierre:     false,
    canSeeAllProjects: false,
    descripcion: 'Accede solo a sus partidas y actividades asignadas.'
  }
}

// Estado de sesión en memoria
let currentUser   = null
let currentRole   = null
let currentPlan   = 'demo'   // 'demo' | 'pro' | 'enterprise'
let onAuthChange  = null

// ── VERIFICAR PERMISO ────────────────────────────────────
export function tienePermiso (permiso) {
  if (!currentRole) return false
  const perms = PERMISOS[currentRole]
  return perms ? (perms[permiso] === true) : false
}

// ── VERIFICAR PLAN ───────────────────────────────────────
export const esPlanActivo = (planRequerido) => {
  const jerarquia = { demo: 0, pro: 1, enterprise: 2 }
  return jerarquia[currentPlan] >= jerarquia[planRequerido]
}

// ── GETTERS ──────────────────────────────────────────────
export const getUser     = () => currentUser
export const getRole     = () => currentRole
export const getRoleInfo = () => PERMISOS[currentRole] || {}
export const getPlan     = () => currentPlan
export const isLoggedIn  = () => currentUser !== null

// ── LOGIN CON FIREBASE ───────────────────────────────────
export async function loginConEmail (email, password, rol, empresa) {
  const { auth, db, ready } = getFirebase()

  if (!ready) {
    // Modo demo sin Firebase
    return loginDemo(email, rol)
  }

  try {
    const cred = await auth.signInWithEmailAndPassword(email, password)
    const uid  = cred.user.uid

    // Obtener datos del usuario de Firestore
    const snap = await db.collection('users').doc(uid).get()
    let userData = snap.exists ? snap.data() : {}

    // Si es nuevo usuario, crear registro
    if (!snap.exists) {
      userData = {
        uid, email, rol, empresa,
        nombre:    email.split('@')[0],
        plan:      'demo',
        proyectos: [],
        createdAt: firebase.firestore.FieldValue.serverTimestamp()
      }
      await db.collection('users').doc(uid).set(userData)
    }

    currentUser = { uid, email, ...userData }
    currentRole = userData.rol || rol
    currentPlan = userData.plan || 'demo'

    console.log(`✅ Login exitoso: ${email} — Rol: ${currentRole} — Plan: ${currentPlan}`)
    return { ok: true, user: currentUser, role: currentRole, plan: currentPlan }

  } catch (err) {
    console.error('❌ Error de login:', err.code, err.message)
    const mensajes = {
      'auth/user-not-found':  'Usuario no encontrado. Verifica tu correo.',
      'auth/wrong-password':  'Contraseña incorrecta.',
      'auth/invalid-email':   'Correo inválido.',
      'auth/too-many-requests': 'Demasiados intentos. Espera un momento.',
      'auth/network-request-failed': 'Sin conexión. Verifica tu red.'
    }
    return { ok: false, error: mensajes[err.code] || 'Error de autenticación.' }
  }
}

// ── LOGIN DEMO (sin Firebase) ────────────────────────────
export async function resetPassword (email) {
  const { auth, ready } = getFirebase()
  await ready
  try {
    await auth.sendPasswordResetEmail(email)
    return { ok: true }
  } catch (e) {
    const msgs = {
      'auth/user-not-found': 'No existe cuenta con ese correo.',
      'auth/invalid-email':  'Correo inválido.',
    }
    return { ok: false, error: msgs[e.code] || 'No se pudo enviar el correo.' }
  }
}

export function loginDemo (email, rol) {
  const nombreBase = email ? email.split('@')[0] : 'Usuario Demo'
  const nombre = nombreBase.replace(/\./g, ' ')
    .split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')

  currentUser = {
    uid:      'demo-' + Date.now(),
    email:    email || 'demo@consultec.mx',
    nombre,
    rol:      rol || ROLES.SUPERVISOR,
    empresa:  'CONSULTEC SA de CV',
    plan:     'demo',
    demo:     true
  }
  currentRole = rol || ROLES.SUPERVISOR
  currentPlan = 'demo'

  console.log('🎭 Modo DEMO activo:', currentUser)
  return { ok: true, user: currentUser, role: currentRole, plan: currentPlan }
}

// ── REGISTRO NUEVO USUARIO ───────────────────────────────
export async function registrarUsuario (nombre, email, password, rol, empresa) {
  const { auth, db, ready } = getFirebase()
  if (!ready) return { ok: false, error: 'Firebase no disponible' }

  try {
    const cred = await auth.createUserWithEmailAndPassword(email, password)
    const uid  = cred.user.uid

    await db.collection('users').doc(uid).set({
      uid, nombre, email, rol, empresa,
      plan:      'demo',
      proyectos: [],
      createdAt: firebase.firestore.FieldValue.serverTimestamp()
    })

    await cred.user.updateProfile({ displayName: nombre })
    return { ok: true, uid }
  } catch (err) {
    return { ok: false, error: err.message }
  }
}

// ── ACTIVAR PLAN ─────────────────────────────────────────
export async function activarPlan (nivel) {
  // En producción: esto se dispara DESPUÉS de pago exitoso con Stripe
  const { db, ready } = getFirebase()
  currentPlan = nivel

  if (ready && currentUser?.uid && !currentUser.demo) {
    await db.collection('users').doc(currentUser.uid).update({ plan: nivel })
  }
  if (currentUser) currentUser.plan = nivel

  console.log(`💳 Plan activado: ${nivel}`)
  return true
}

// ── LOGOUT ───────────────────────────────────────────────
export async function logout () {
  const { auth, ready } = getFirebase()
  if (ready) await auth.signOut().catch(console.error)
  currentUser = null
  currentRole = null
  currentPlan = 'demo'
  console.log('👋 Sesión cerrada')
}

// ── ESCUCHAR CAMBIOS DE AUTH ─────────────────────────────
export function onAuthStateChanged (callback) {
  onAuthChange = callback
  const { auth, db, ready } = getFirebase()
  if (!ready) return () => {}

  return auth.onAuthStateChanged(async (user) => {
    if (user) {
      const snap = await db.collection('users').doc(user.uid).get()
      if (snap.exists) {
        const data = snap.data()
        currentUser = { uid: user.uid, email: user.email, ...data }
        currentRole = data.rol
        currentPlan = data.plan || 'demo'
      }
    } else {
      currentUser = null
      currentRole = null
    }
    if (callback) callback(currentUser)
  })
}
